import { cn } from '@/lib/utils';
import { omit } from 'lodash';
import { memo, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { PluggableList } from 'react-markdown/lib';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { visit } from 'unist-util-visit';

import { ChainlitContext, type IMessageElement } from '@chainlit/react-client';

import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

import BlinkingCursor from './BlinkingCursor';
import CodeSnippet from './CodeSnippet';
import { ElementRef } from './Elements/ElementRef';
import {
  type AlertProps,
  MarkdownAlert,
  alertComponents,
  normalizeAlertType
} from './MarkdownAlert';
import { remarkSourcesPlugin, sourcesComponents } from './MarkdownSources';

// Hoisted to module scope so the `components` object passed to ReactMarkdown
// has stable references for renderers that don't depend on hook-scoped values.
// `a` and `img` are built inside the component because they depend on
// refElements / apiClient respectively.
const STATIC_COMPONENTS = {
  code(props: any) {
    return (
      <code
        {...omit(props, ['node'])}
        className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold [overflow-wrap:anywhere]"
      />
    );
  },
  pre({ children: _children, ...props }: any) {
    return <CodeSnippet {...props} />;
  },
  blockquote(props: any) {
    return (
      <blockquote
        {...omit(props, ['node'])}
        className="mt-6 border-l-2 pl-6 italic"
      />
    );
  },
  em(props: any) {
    return <span {...omit(props, ['node'])} className="italic" />;
  },
  strong(props: any) {
    return <span {...omit(props, ['node'])} className="font-bold" />;
  },
  hr() {
    return <Separator />;
  },
  ul(props: any) {
    return (
      <ul
        {...omit(props, ['node'])}
        className="my-3 ml-3 list-disc pl-2 [&>li]:mt-1"
      />
    );
  },
  ol(props: any) {
    return (
      <ol
        {...omit(props, ['node'])}
        className="my-3 ml-3 list-decimal pl-2 [&>li]:mt-1"
      />
    );
  },
  h1(props: any) {
    return (
      <h1
        {...omit(props, ['node'])}
        className="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl mt-8 first:mt-0"
      />
    );
  },
  h2(props: any) {
    return (
      <h2
        {...omit(props, ['node'])}
        className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight mt-8 first:mt-0"
      />
    );
  },
  h3(props: any) {
    return (
      <h3
        {...omit(props, ['node'])}
        className="scroll-m-20 text-2xl font-semibold tracking-tight mt-6 first:mt-0"
      />
    );
  },
  h4(props: any) {
    return (
      <h4
        {...omit(props, ['node'])}
        className="scroll-m-20 text-xl font-semibold tracking-tight mt-6 first:mt-0"
      />
    );
  },
  p(props: any) {
    return (
      <div
        {...omit(props, ['node'])}
        className="leading-7 [&:not(:first-child)]:mt-4 whitespace-pre-wrap break-words"
        role="article"
      />
    );
  },
  table({ children, ...props }: any) {
    return (
      <Card className="[&:not(:first-child)]:mt-2 [&:not(:last-child)]:mb-2">
        <Table {...(props as any)}>{children}</Table>
      </Card>
    );
  },
  thead({ children, ...props }: any) {
    return <TableHeader {...(props as any)}>{children}</TableHeader>;
  },
  tr({ children, ...props }: any) {
    return <TableRow {...(props as any)}>{children}</TableRow>;
  },
  th({ children, ...props }: any) {
    return <TableHead {...(props as any)}>{children}</TableHead>;
  },
  td({ children, ...props }: any) {
    return <TableCell {...(props as any)}>{children}</TableCell>;
  },
  tbody({ children, ...props }: any) {
    return <TableBody {...(props as any)}>{children}</TableBody>;
  },
  blinkingCursor: () => <BlinkingCursor whitespace />,
  alert: ({
    type,
    children,
    ...props
  }: AlertProps & { type?: string; children?: any }) => {
    const alertType = normalizeAlertType(type || props.variant || 'info');
    return alertComponents.Alert({ variant: alertType, children });
  }
};

interface Props {
  allowHtml?: boolean;
  latex?: boolean;
  renderMarkdown?: boolean;
  refElements?: IMessageElement[];
  children: string;
  className?: string;
}

const cursorPlugin = () => {
  return (tree: any) => {
    visit(tree, 'text', (node: any, index, parent) => {
      const placeholderPattern = /\uE000/g;
      const matches = [...(node.value?.matchAll(placeholderPattern) || [])];

      if (matches.length > 0) {
        const newNodes: any[] = [];
        let lastIndex = 0;

        matches.forEach((match) => {
          const [fullMatch] = match;
          const startIndex = match.index!;
          const endIndex = startIndex + fullMatch.length;

          if (startIndex > lastIndex) {
            newNodes.push({
              type: 'text',
              value: node.value!.slice(lastIndex, startIndex)
            });
          }

          newNodes.push({
            type: 'blinkingCursor',
            data: {
              hName: 'blinkingCursor',
              hProperties: { text: 'Blinking Cursor' }
            }
          });

          lastIndex = endIndex;
        });

        if (lastIndex < node.value!.length) {
          newNodes.push({
            type: 'text',
            value: node.value!.slice(lastIndex)
          });
        }

        parent!.children.splice(index, 1, ...newNodes);
      }
    });
  };
};

const MarkdownImpl = ({
  allowHtml,
  latex,
  renderMarkdown,
  refElements,
  className,
  children
}: Props) => {
  const apiClient = useContext(ChainlitContext);

  const rehypePlugins = useMemo(() => {
    let rehypePlugins: PluggableList = [];
    if (allowHtml) {
      rehypePlugins = [rehypeRaw as any, ...rehypePlugins];
    }
    if (latex) {
      rehypePlugins = [rehypeKatex as any, ...rehypePlugins];
    }
    return rehypePlugins;
  }, [allowHtml, latex]);

  const remarkPlugins = useMemo(() => {
    let remarkPlugins: PluggableList = [
      cursorPlugin,
      remarkGfm as any,
      remarkDirective as any,
      MarkdownAlert,
      remarkSourcesPlugin
    ];

    if (latex) {
      remarkPlugins = [...remarkPlugins, remarkMath as any];
    }
    return remarkPlugins;
  }, [latex]);

  const components = useMemo(
    () => ({
      ...alertComponents,
      ...sourcesComponents,
      ...STATIC_COMPONENTS,
      a({ children, ...props }: any) {
        const name = children as string;
        const element = refElements?.find((e) => e.name === name);
        if (element) {
          return <ElementRef element={element} />;
        }
        // External http(s) links get a small favicon prefixed via Google's
        // S2 service. onError hides the <img> if S2 has no record for the
        // domain so the link still renders cleanly.
        let favicon: string | null = null;
        try {
          if (props.href && /^https?:\/\//i.test(props.href)) {
            const u = new URL(props.href);
            favicon = `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=32`;
          }
        } catch {
          favicon = null;
        }
        return (
          <a
            {...props}
            className="text-primary hover:underline [overflow-wrap:anywhere]"
            target="_blank"
          >
            {favicon && (
              <img
                src={favicon}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="mr-1 inline-block h-3.5 w-3.5 rounded-sm align-text-bottom"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            {children}
          </a>
        );
      },
      img: (image: any) => {
        // Check if the image source is actually a video file
        const src = image.src.startsWith('/public')
          ? apiClient.buildEndpoint(image.src)
          : image.src;

        // Favicon images (Google S2 service) — render inline-small, not as
        // a 16:9 card. Used by step progress lines and the link renderer.
        if (
          typeof src === 'string' &&
          src.startsWith('https://www.google.com/s2/favicons?')
        ) {
          return (
            <img
              src={src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="inline-block h-3.5 w-3.5 rounded-sm align-text-bottom"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        }

        const videoExtensions = [
          '.mp4',
          '.webm',
          '.mov',
          '.avi',
          '.ogv',
          '.m4v'
        ];
        const isVideo = videoExtensions.some((ext) =>
          src.toLowerCase().split(/[?#]/)[0].endsWith(ext)
        );

        if (isVideo) {
          return (
            <div className="sm:max-w-sm md:max-w-md">
              <video
                src={src}
                controls
                className="w-full h-auto rounded-md"
                style={{ maxWidth: '100%' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          );
        }

        return (
          <div className="sm:max-w-sm md:max-w-md">
            <AspectRatio
              ratio={16 / 9}
              className="bg-muted rounded-md overflow-hidden"
            >
              <img
                src={src}
                alt={image.alt}
                className="h-full w-full object-contain"
              />
            </AspectRatio>
          </div>
        );
      }
    }),
    [refElements, apiClient]
  );

  if (renderMarkdown === false) {
    return (
      <pre
        className={cn('whitespace-pre-wrap break-words', className)}
        style={{ fontFamily: 'inherit' }}
      >
        {children}
      </pre>
    );
  }

  return (
    <ReactMarkdown
      className={cn('prose lg:prose-xl', className)}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {children}
    </ReactMarkdown>
  );
};

const refElementsEqual = (
  a?: IMessageElement[],
  b?: IMessageElement[]
): boolean => {
  if (a === b) return true;
  const al = a?.length ?? 0;
  const bl = b?.length ?? 0;
  if (al !== bl) return false;
  if (al === 0) return true;
  for (let i = 0; i < al; i++) {
    if (a![i].id !== b![i].id) return false;
  }
  return true;
};

const Markdown = memo(
  MarkdownImpl,
  (prev, next) =>
    prev.children === next.children &&
    prev.allowHtml === next.allowHtml &&
    prev.latex === next.latex &&
    prev.renderMarkdown === next.renderMarkdown &&
    prev.className === next.className &&
    refElementsEqual(prev.refElements, next.refElements)
);

export { Markdown };
